import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { useUpdateOrganizationMfaRequirement } from '@tinct/core-api';
import { toast } from 'sonner';

type MfaRequirementModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  /** The state being confirmed: `true` requires two-factor authentication, `false` lifts it. */
  required: boolean;
  /**
   * Accepted members without two-factor authentication. Only a workspace admin or a back-office
   * super admin can count them (the API hides `mfaEnabled` from everyone else): leave it out otherwise.
   */
  membersWithoutMfaCount?: number | null;
  /** The member list was longer than what was counted. */
  membersCountIsPartial?: boolean;
};

const NEXT_SIGN_IN =
  'at their next sign-in, within 24 hours at most for people who are currently signed in';

function describeRequire(count: number | null | undefined, isPartial: boolean) {
  if (count == null) {
    return `Members without two-factor authentication get an email about it, and will be asked to set it up ${NEXT_SIGN_IN}.`;
  }
  if (count === 0 && !isPartial) {
    return 'Every active member already has two-factor authentication. People joining later will be asked to set it up when they sign in.';
  }
  const members = `${isPartial ? 'At least ' : ''}${count} active ${count === 1 ? 'member does' : 'members do'}`;
  return `${members} not have two-factor authentication yet. They get an email about it, and will be asked to set it up ${NEXT_SIGN_IN}.`;
}

/**
 * Confirms switching the workspace's two-factor authentication requirement. Surface-agnostic (no
 * workspace hooks): used by the workspace security page and the back-office profile tab.
 */
export const MfaRequirementModal = ({
  open,
  onOpenChange,
  organizationId,
  required,
  membersWithoutMfaCount,
  membersCountIsPartial = false,
}: MfaRequirementModalProps) => {
  const updateRequirement = useUpdateOrganizationMfaRequirement({
    onSuccess: () => {
      toast.success(
        required
          ? 'Two-factor authentication is now required'
          : 'Two-factor authentication is no longer required',
      );
      onOpenChange(false);
    },
    // The API is the gate: 403 for anyone but a workspace admin, 409 when the caller requiring it
    // has no second factor of their own. Its message says which, a retry would not help.
    onError: (error) => {
      toast.error(error.message || 'Could not update the two-factor authentication requirement');
      onOpenChange(false);
    },
  });

  const handleConfirm = () => {
    updateRequirement.mutate({ organizationId, required });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {required
              ? 'Require two-factor authentication?'
              : 'Stop requiring two-factor authentication?'}
          </DialogTitle>
          <DialogDescription>
            {required
              ? describeRequire(membersWithoutMfaCount, membersCountIsPartial)
              : 'Members keep the two-factor authentication they have set up, but they will be able to disable it, and new members will no longer be asked to set it up.'}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={required ? 'default' : 'destructive'}
            onClick={handleConfirm}
            isLoading={updateRequirement.isPending}
          >
            {required ? 'Require 2FA' : 'Stop requiring 2FA'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
